import React, { useState, useEffect } from "react";
import API from "../../api/axiosConfig";
import { toast } from "react-toastify";
import { User, X, UserPlus, QrCode } from "lucide-react";
import QrScannerModal from "./QrScannerModal";

const inp = "w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400 bg-white";
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function displayFromValue(value) {
  if (value?.customerId) return { type: "account", id: value.customerId, name: value.customerLabel ?? "" };
  if (value?.customerName) return { type: "guest", name: value.customerName };
  return null;
}

// Selector de cliente para una mesa: el QR que el cliente ya tiene en "Mi cuenta"
// (codifica su id) es la ÚNICA forma de identificarlo como usuario registrado — así
// gana qpoints al cobrar. Si no tiene cuenta o no se puede escanear, se escribe
// cualquier nombre libre para identificar la cuenta, sin consultar la base de datos
// y sin sumar qpoints — solo quedan registrados (y ganan puntos) los clientes que
// escanearon su QR.
//
// El estado que se MUESTRA vive localmente (display) y solo se resincroniza con
// `value` cuando el padre cambia de identidad (otra mesa/venta) — no en cada render.
// "Cambiar" (la X) solo limpia la pantalla para elegir de nuevo, nunca dispara
// onChange por su cuenta: el backend exige exactamente uno de los dos (cuenta real o
// nombre libre), así que avisar con ambos vacíos solo porque el usuario quiere volver
// a elegir rompía la edición — el padre rechazaba el cambio y la pantalla volvía a
// mostrar el valor viejo, dando la impresión de que no se podía editar nada.
export default function CustomerPicker({ value, onChange }) {
  const [scannerOpen, setScannerOpen] = useState(false);
  const [guestName, setGuestName] = useState("");
  const [display, setDisplay] = useState(() => displayFromValue(value));

  useEffect(() => {
    setDisplay(displayFromValue(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.customerId, value?.customerLabel, value?.customerName]);

  const clearSelection = () => {
    setDisplay(null);
    setGuestName("");
  };

  const commitGuestName = () => {
    const name = guestName.trim();
    if (!name) return;
    setDisplay({ type: "guest", name });
    setGuestName("");
    onChange({ customerId: undefined, customerName: name });
  };

  const handleScan = async (decodedText) => {
    setScannerOpen(false);
    const scanned = decodedText.trim();
    if (!UUID_RE.test(scanned)) {
      toast.error("Ese código QR no corresponde a un cliente de Qkiere");
      return;
    }
    try {
      const res = await API.get("/users/search-customer", { params: { q: scanned } });
      const users = res.data?.data?.users ?? [];
      if (users.length === 0) {
        toast.error("No se encontró ningún cliente con ese código");
        return;
      }
      const u = users[0];
      setDisplay({ type: "account", id: u.id, name: u.name, phone: u.phone });
      setGuestName("");
      onChange({ customerId: u.id, customerName: undefined, customerLabel: u.name });
      toast.success(`Cliente identificado: ${u.name}`);
    } catch {
      toast.error("Error al validar el código");
    }
  };

  if (display?.type === "account") {
    return (
      <div className="flex items-center gap-2 border border-gray-200 rounded-xl px-3 py-2.5 bg-supernova-50">
        <User size={15} className="text-supernova-600 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-800 truncate">{display.name}</p>
          {display.phone && <p className="text-xs text-gray-500">{display.phone}</p>}
        </div>
        <button type="button" onClick={clearSelection} className="text-gray-400 hover:text-gray-700 shrink-0" title="Cambiar cliente">
          <X size={15} />
        </button>
      </div>
    );
  }

  if (display?.type === "guest") {
    return (
      <div className="flex items-center gap-2 border border-gray-200 rounded-xl px-3 py-2.5 bg-gray-50">
        <UserPlus size={15} className="text-gray-500 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-800 truncate">{display.name}</p>
          <p className="text-xs text-gray-400">Cliente sin cuenta · no suma Qpuntos</p>
        </div>
        <button type="button" onClick={clearSelection} className="text-gray-400 hover:text-gray-700 shrink-0" title="Cambiar cliente">
          <X size={15} />
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={() => setScannerOpen(true)}
        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-supernova-500 hover:bg-supernova-600 text-white text-sm font-bold transition"
      >
        <QrCode size={16} /> Escanear código QR
      </button>

      <div className="flex items-center gap-2">
        <span className="flex-1 border-t border-gray-200" />
        <span className="text-[11px] text-gray-400">o</span>
        <span className="flex-1 border-t border-gray-200" />
      </div>

      <div className="flex gap-2">
        <input
          className={inp}
          value={guestName}
          onChange={(e) => setGuestName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitGuestName(); } }}
          placeholder="Nombre (sin cuenta)"
        />
        <button
          type="button"
          onClick={commitGuestName}
          disabled={!guestName.trim()}
          className="px-4 rounded-xl bg-gray-800 hover:bg-gray-900 text-white text-sm font-semibold disabled:opacity-40 shrink-0 transition"
        >
          Usar
        </button>
      </div>
      <p className="text-[11px] text-gray-400">Un cliente sin cuenta no suma Qpuntos.</p>

      {scannerOpen && <QrScannerModal onScan={handleScan} onClose={() => setScannerOpen(false)} />}
    </div>
  );
}
